import { FC, useCallback, useMemo, useState, useEffect } from 'react'
import { AdvisorRequestPanePreset } from 'components/features/coaching/advisorMeeting/AdvisorRequestPanePreset/AdvisorRequestPanePreset'
import { AdvisorMeetingWizardFields, AdvisorRequestNotes } from 'components/features/coaching/advisorMeeting/types/requests'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { createAdvisorMeetingActionCreator } from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { createAdvisorSelector, whetherToSkipSignUpStepSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import { Advisor } from 'components/features/coaching/advisor/types/advisor'
import { MeetingDetails } from 'components/features/coaching/advisorPage/MeetingDetails/MeetingDetails'
import Button from 'components/features/pfm/Button/Button'
import { useForm, FormProvider } from 'react-hook-form'
import { isMemberPremiumSelector } from 'selectors/globalSelectors'
import { AdvisorSignupPane } from 'components/features/coaching/advisorMeeting/types/floatPane'

type AdvisorRequestConfirmProps = {
  fields: AdvisorMeetingWizardFields
  onClose: () => void
}
export const AdvisorRequestConfirm: FC<AdvisorRequestConfirmProps> = (props) => {
  const { onClose, fields: initialFields } = props
  const [fields, setFields] = useState(initialFields)
  const { advisor_uid } = fields

  const withUid = useMemo(() => ({ uid: advisor_uid }), [advisor_uid])
  const dispatch = useStoreDispatch()
  const advisorSelector = useMemo(createAdvisorSelector, [])
  const advisor = useStoreSelector((state) => advisorSelector(state, withUid))
  const whetherToSkipSignUpStep = useStoreSelector(whetherToSkipSignUpStepSelector)

  const formContext = useForm<{ notes: string }>({
    defaultValues: {
      notes: fields.notes,
    },
  })
  const { watch } = formContext
  const notes = watch('notes')
  useEffect(() => {
    setFields((prevState) => ({
      ...prevState,
      notes,
    }))
  }, [notes])

  const handleBack = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('advisor', { mode: 'request', step: 'schedule', fields }))
  }, [fields, dispatch])

  const isMemberPremium = useStoreSelector(isMemberPremiumSelector)
  const handleConfirm = useCallback(() => {
    if (!whetherToSkipSignUpStep) {
      dispatch(modifyFloatingPaneActionCreator('advisor', { mode: 'request',
        step: 'signup',
        fields: {
          ...fields,
          isSignUpStepSucceed: true,
        }} as AdvisorSignupPane))
      return
    }

    if (!isMemberPremium) {
      dispatch(modifyFloatingPaneActionCreator('advisor', { mode: 'request', step: 'subscription', fields }))
      return
    }

    dispatch(modifyFloatingPaneActionCreator('advisor', { mode: 'request', step: 'booking' }))
    dispatch(createAdvisorMeetingActionCreator.request(fields))
  }, [fields, dispatch, isMemberPremium, whetherToSkipSignUpStep])

  const secondaryButton = (
    <Button text='Back' style='hollow' qaMarker='back' fullWidth onClick={ handleBack } />
  )

  return (
    <AdvisorRequestPanePreset
      title='Schedule a New Coaching Session'
      subTitle='3. Confirm your session'
      actionName='CONTINUE'
      progressBarPercent={ 75 }
      onSuccess={ handleConfirm }
      onCancel={ onClose }
      secondaryButton={ secondaryButton }
    >
      { advisor && (
        <FormProvider { ...formContext }>
          <SessionCard advisor={ advisor } fields={ fields } />
        </FormProvider>
      )}
    </AdvisorRequestPanePreset>
  )
}

type SessionCardProps = {
  advisor: Advisor
  fields: AdvisorMeetingWizardFields
}
const SessionCard: FC<SessionCardProps> = (props) => {
  const { advisor, fields } = props
  const { uid } = advisor
  const { topics, notes, timeslot } = fields

  const noteChild: AdvisorRequestNotes = {
    question: '',
    answer: notes,
  }

  return (
    <MeetingDetails
      advisorUid={ uid }
      topics={ topics }
      notes={ noteChild }
      timeslot={ timeslot }
    />
  )
}
